/**
 * TenantStatusModal Component
 * Modal for changing tenant status
 */

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { Modal, Button } from '../../../components/common';
import { useUpdateTenantStatus } from '../../../hooks/useSuperAdmin';
import type { TenantListItem, TenantStatusType } from '../../../types/superadmin';

interface TenantStatusModalProps {
  tenant: TenantListItem;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const statusOptions: {
  value: TenantStatusType;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
}[] = [
  {
    value: 'active',
    label: 'Active',
    description: 'Full access to the platform',
    icon: CheckCircle,
    color: 'text-success-600 bg-success-50 border-success-200',
  },
  {
    value: 'trial',
    label: 'Trial',
    description: 'Limited time trial period',
    icon: Clock,
    color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  },
  {
    value: 'suspended',
    label: 'Suspended',
    description: 'Access temporarily disabled',
    icon: XCircle,
    color: 'text-danger-600 bg-danger-50 border-danger-200',
  },
];

export const TenantStatusModal: React.FC<TenantStatusModalProps> = ({
  tenant,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<TenantStatusType>(
    tenant.status
  );
  const [reason, setReason] = useState('');

  const updateStatusMutation = useUpdateTenantStatus();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateStatusMutation.mutateAsync({
        tenantId: tenant.id,
        data: {
          status: selectedStatus,
          reason: reason.trim() || undefined,
        },
      });
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const showWarning = selectedStatus === 'suspended' && tenant.status !== 'suspended';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Tenant Status">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Status */}
        <div className="p-4 bg-stone-100 rounded-lg">
          <p className="text-sm text-stone-500">Current Status</p>
          <p className="text-lg font-medium text-stone-900 capitalize">
            {tenant.name} - {tenant.status}
          </p>
        </div>

        {/* Status Options */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-stone-700">
            New Status
          </label>
          {statusOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedStatus === option.value;
            const isCurrent = tenant.status === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedStatus(option.value)}
                className={clsx(
                  'w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left',
                  isSelected
                    ? option.color + ' border-current'
                    : 'bg-white border-stone-200 hover:border-stone-300'
                )}
              >
                <Icon
                  className={clsx(
                    'w-6 h-6',
                    isSelected ? 'text-current' : 'text-stone-400'
                  )}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'font-medium',
                        isSelected ? 'text-current' : 'text-stone-900'
                      )}
                    >
                      {option.label}
                    </span>
                    {isCurrent && (
                      <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-stone-500">{option.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Reason (optional) */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">
            Reason (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter a reason for the status change..."
            rows={3}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Warning for suspension */}
        {showWarning && (
          <div className="flex items-start gap-3 p-4 bg-danger-50 border border-danger-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-danger-800">Warning</p>
              <p className="text-sm text-danger-600">
                Suspending this tenant will immediately block all {tenant.userCount} users
                from accessing the platform.
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={selectedStatus === 'suspended' ? 'danger' : 'primary'}
            disabled={
              updateStatusMutation.isPending || selectedStatus === tenant.status
            }
          >
            {updateStatusMutation.isPending
              ? 'Updating...'
              : `Change to ${selectedStatus}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
